/**
 * One row of the pool Settings Awards card: a color disc, the type name, the
 * grant line, and exactly one trailing control. The row body opens edit
 * whether the type is active or archived (R9, SUR-R92 clause 3); only the
 * trailing control differs by `variant`.
 *
 * Structure note: the frame markup nests a clickable span inside a button,
 * which is invalid HTML and breaks accessible-name computation. Here the row
 * body button and the trailing action button are siblings in a flex `<li>`.
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { awardColorRole, awardSwatchClass } from "../awards/awardChips";
import {
  AWARDS_ROW_DELETE_LABEL,
  AWARDS_ROW_RESTORE_LABEL,
  awardsArchivedGrantLine,
  awardsGrantLine,
  awardsRestoreBlockedReason,
  type typeAwardKind,
} from "./poolAwardsCopy";
import type { typeAwardTypeRow } from "./poolAwardsModel";

export type typeAwardTypeRowProps = {
  row: typeAwardTypeRow;
  kind: typeAwardKind;
  variant: "active" | "archived";
  /** Only read when variant is "archived". */
  canRestore?: boolean | undefined;
  isBusy: boolean;
  onOpenEdit: (awardTypeId: string) => void;
  onRemove: (awardTypeId: string) => void;
  onRestore: (awardTypeId: string) => void;
};

export const AwardTypeRow = ({
  row,
  kind,
  variant,
  canRestore,
  isBusy,
  onOpenEdit,
  onRemove,
  onRestore,
}: typeAwardTypeRowProps): ReactNode => {
  const grantLine =
    variant === "active"
      ? awardsGrantLine(row)
      : awardsArchivedGrantLine({
          kind,
          grantCount: row.grantCount,
          memberCount: row.memberCount,
        });
  const showRestoreReason = variant === "archived" && canRestore !== true;

  return (
    <li className="flex items-center gap-3 py-3">
      <span
        aria-hidden="true"
        className={`h-8 w-8 shrink-0 rounded-full ${awardSwatchClass(awardColorRole(row.color))}`}
      />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          data-testid={`award-type-row-${row.awardTypeId}`}
          className="block w-full text-left"
          onClick={() => {
            onOpenEdit(row.awardTypeId);
          }}
        >
          <span className="block truncate text-body-strong text-ink">
            {row.name}
          </span>
          <span className="block text-caption text-ink-secondary">
            {grantLine}
          </span>
        </button>
        {showRestoreReason ? (
          <p
            className="text-caption text-warning"
            data-testid={`award-type-restore-reason-${row.awardTypeId}`}
          >
            {awardsRestoreBlockedReason(kind)}
          </p>
        ) : null}
      </div>
      {variant === "active" ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`award-type-remove-${row.awardTypeId}`}
          disabled={isBusy}
          onClick={() => {
            onRemove(row.awardTypeId);
          }}
        >
          {AWARDS_ROW_DELETE_LABEL}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`award-type-restore-${row.awardTypeId}`}
          disabled={isBusy || canRestore !== true}
          onClick={() => {
            onRestore(row.awardTypeId);
          }}
        >
          {AWARDS_ROW_RESTORE_LABEL}
        </Button>
      )}
    </li>
  );
};
